/**
 * Inbox display helpers — resolve the wire keys on an InboxItem to the labels a
 * field user reads, and format the row "age". Oxbo-Z shows labels but keeps the
 * keys (§3): the well name resolves from the Oxbo-M register (already cached by
 * the Well Explorer) and the supervisor from its personnel entry_key.
 */
import type { WellView } from "@api/types";
import type { Locale } from "@i18n/locales";
import { intlTag } from "@lib/format";

/** event_type → the stable i18n key. Unknown types render their raw value. */
export type EventTypeFilter = "drilling" | "completion" | "workover" | "intervention";

export const EVENT_TYPE_FILTERS: EventTypeFilter[] = [
  "drilling",
  "completion",
  "workover",
  "intervention",
];

/**
 * Humanise a personnel entry_key (e.g. `sup.halvorsen` → `Halvorsen`) as a
 * graceful fallback until the personnel catalog is wired. The real label comes
 * from Oxbo-M; this only makes the seed keys readable, never invents a name.
 */
export function supervisorLabel(entryKey: string | null): string {
  if (!entryKey) return "—";
  const tail = entryKey.split(".").pop() ?? entryKey;
  return tail.charAt(0).toUpperCase() + tail.slice(1);
}

/** Well common-name from the register, keyed by UWI; falls back to the UWI. */
export function wellName(uwi: string, byUwi: Map<string, WellView>): string {
  return byUwi.get(uwi)?.commonName ?? uwi;
}

/**
 * Age of a report as a compact, locale-formatted relative label. `date_report`
 * is a calendar date (YYYY-MM-DD); we compare against today at day-granularity,
 * showing hours only for same-day rows so the freshest work reads as "today".
 */
export function reportAge(dateReport: string, locale: Locale, now: Date = new Date()): string {
  const rtf = new Intl.RelativeTimeFormat(intlTag(locale), { numeric: "auto" });
  const then = new Date(`${dateReport}T00:00:00`);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today.getTime() - then.getTime()) / 86_400_000);
  if (days <= 0) return rtf.format(0, "day"); // "today"
  return rtf.format(-days, "day");
}
